import type { UI } from '@/v2/types';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Box, Container, Flex, Image } from '@qga/roo-ui/components';

import type { PackageOption as PackageOptionType } from '@shared/v2/utils/getPackageOptions/getPackageOptions';
import CollapsiblePackageSection, {
  Heading,
} from '@shared/v2/components/CollapsiblePackageSection';
import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import useAutoSelectBestRates from '@shared/v2/hooks/useAutoSelectBestRates';

import Button from '@/components/Button';
import RooUiHeading from '@/components/Heading';
import PackageHeroSummary from '@/components/PackageHeroSummary';
import PricePerTraveller from '@/components/PricePerTraveller/PricePerTraveller';
import Text from '@/components/Text';
import getAsset from '@/utils/getAsset';
import { openPackageOptionPanelDataLayerEvent } from '@/v2/components/pages/PackageViewV2/utils/dataLayer';
import PackageDetails from '@/v2/components/ui/PackageDetails';
import {
  mapInclusion,
  PackageInclusions,
} from '@/v2/components/ui/PackageInclusions';
import PackageOption from '@/v2/components/ui/PackageOption';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';
import PackageOptionSlider from './ui/PackageOptionSlider';

interface Props {
  isPanelOpen: boolean;
  isPackagePageV3Enabled?: boolean;
  setIsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  packageOptions: PackageOptionType[];
  selectedInclusionSetId?: string;
  onSelectPackageOption: (inclusionId: string) => void;
  propertyContent: UI.Property;
  procuredOffer: UI.ProcuredOffer | null;
  disabled: boolean;
}

const StyledButton = styled(Button)`
  background: transparent;
  height: 48px;
  font-size: 15px;
`;

const ColumnFlex = styled(Flex)`
  flex-direction: column;
`;

const StyledSection = styled(CollapsiblePackageSection)`
  overflow-x: hidden;
`;

const PackageOptionContainer = ({
  isPanelOpen,
  isPackagePageV3Enabled,
  setIsPanelOpen,
  packageOptions,
  selectedInclusionSetId,
  onSelectPackageOption,
  propertyContent,
  procuredOffer,
  disabled,
}: Props) => {
  const { isReady } = useRouter();
  const shouldUseNewEvents = useGA4Events();

  const selectedInclusion = packageOptions.find(
    (inclusion) => inclusion.id === selectedInclusionSetId,
  );

  const activeTitle =
    selectedInclusionSetId === BEST_RATE_ID && !selectedInclusion?.title
      ? 'Best Available Rates'
      : selectedInclusion?.title;

  const isSelectedId = (inclusionSetId: string) =>
    inclusionSetId === selectedInclusionSetId;

  const hasDoneAutoSelect = useAutoSelectBestRates(packageOptions?.length > 0);

  useEffect(() => {
    if (isReady && hasDoneAutoSelect && !isPackagePageV3Enabled) {
      setIsPanelOpen(!selectedInclusionSetId);
    }
  }, [
    hasDoneAutoSelect,
    isPackagePageV3Enabled,
    isReady,
    selectedInclusionSetId,
    setIsPanelOpen,
  ]);

  const togglePanel = () => {
    setIsPanelOpen((curr) => {
      if (!curr) {
        openPackageOptionPanelDataLayerEvent();
        if (shouldUseNewEvents) {
          dataLayer.ctaClickEvent({
            itemType: 'button',
            itemText: 'Change package option',
          });
        }
      }
      return !curr;
    });
  };

  const termsAndConditionLink = (inclusionSet: PackageOptionType) => {
    return (
      <Flex>
        <PackageDetails
          data-testid="PACKAGE_TERM_DETAILS_MODAL"
          propertyContent={propertyContent}
          procuredOffer={procuredOffer ?? undefined}
          leadInOffer={inclusionSet.cheapestOffer.leadInOffer}
          inclusions={inclusionSet.inclusions}
        />
      </Flex>
    );
  };

  const packageOffers = (inclusionSet: PackageOptionType) => {
    const { displayTravellerTypes } = inclusionSet.cheapestOffer.leadInOffer;
    const { nights, travellers, price, origin } =
      inclusionSet.cheapestOffer.cheapestRoute;

    return (
      <PackageOption
        selected={isSelectedId(inclusionSet.id)}
        key={inclusionSet.id}
        title={inclusionSet.title}
        onClickCTA={() => {
          if (shouldUseNewEvents) {
            dataLayer.tileClickEvent({
              groupName: 'Package options',
              itemName: inclusionSet?.title || 'Best Available Rates',
              itemText: 'SELECT PACKAGE',
              index: inclusionSet?.optionNumber || 1,
            });
            dataLayer.ctaClickEvent({
              itemText: 'Select package',
            });
          }
          onSelectPackageOption(inclusionSet.id);
        }}
        pillText={inclusionSet.endDateMessage}
        disabled={disabled}
      >
        <ColumnFlex gap="16px">
          <ColumnFlex gap="8px">
            <Box>
              {price && (
                <>
                  <PackageHeroSummary
                    exact={false}
                    nights={nights}
                    showNights
                    justifyContent="flex-start"
                  />
                  <PricePerTraveller
                    displayTravellerTypes={displayTravellerTypes}
                    perTraveller={price.perTraveller}
                    travellers={travellers}
                    total={price.total}
                    valuedAtTotal={price.valuedAtTotal}
                    alignContent={'flex-start'}
                    exactTotalPrice
                  />
                </>
              )}
              {!price && (
                <RooUiHeading.h5 color={'brand.primary'}>
                  Select travel dates for package pricing
                </RooUiHeading.h5>
              )}
            </Box>
            <Text
              fontSize={['14px', null, '16px']}
              fontWeight="400"
              lineHeight="20px"
            >
              Departing {origin?.name}
            </Text>
          </ColumnFlex>
          <ColumnFlex gap="8px">
            <PackageInclusions
              inclusions={inclusionSet.inclusions.map(mapInclusion)}
              numberToShow={4}
              showSubtitle={false}
            />
            {termsAndConditionLink(inclusionSet)}
          </ColumnFlex>
        </ColumnFlex>
      </PackageOption>
    );
  };

  const getPackageOptions = () => {
    const bestAvailableRate = (
      <PackageOption.BestAvailableRates
        selected={isSelectedId(BEST_RATE_ID)}
        onClickCTA={() => onSelectPackageOption(BEST_RATE_ID)}
        pillText={null}
        disabled={disabled}
        key={BEST_RATE_ID}
      />
    );

    const procuredOfferItems = packageOptions?.map(
      (inclusionSet: PackageOptionType) => packageOffers(inclusionSet),
    );

    return [bestAvailableRate, ...procuredOfferItems];
  };

  return (
    <StyledSection
      icon={
        <Image
          src={getAsset('/images/icons/ic_on_sale.svg')}
          alt="Sale icon"
          width={['42px', null, '56px']}
        />
      }
      headerContent={
        <ColumnFlex gap="4px">
          {isPanelOpen ? (
            <>
              <RooUiHeading.h4>Package options</RooUiHeading.h4>
              <Heading>Select your package option</Heading>
            </>
          ) : (
            <>
              <Heading>Package options</Heading>
              <RooUiHeading.h4 data-testid="SELECTED_PACKAGE_OPTION_HEADING">
                {activeTitle ?? ''}
              </RooUiHeading.h4>
              {selectedInclusion && termsAndConditionLink(selectedInclusion)}
            </>
          )}
        </ColumnFlex>
      }
      sectionContent={
        <Container data-testid="PACKAGE_OPTION_CONTAINER" padding={0}>
          <PackageOptionSlider
            data-testid="PACKAGE_OPTION_SLIDER"
            initialKey={selectedInclusionSetId}
          >
            {getPackageOptions()}
          </PackageOptionSlider>
        </Container>
      }
      collapseButton={
        <Box>
          <StyledButton
            data-testid="CHANGE_PACKAGE_OPTION_CTA"
            width={['100%', null, '240px']}
            variant="secondary"
            outline={!isPanelOpen}
            onClick={togglePanel}
          >
            {isPanelOpen
              ? isPackagePageV3Enabled
                ? 'Done'
                : 'Cancel'
              : 'Change package option'}
          </StyledButton>
        </Box>
      }
      isOpen={isPanelOpen}
      showButton={!isPanelOpen || (isPanelOpen && !!selectedInclusionSetId)}
    />
  );
};

export default PackageOptionContainer;
